if (!customElements.get('input-modal')) {
  customElements.define(
    'input-modal',
    class InputModal extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.productFormId;
        this.boundClose = this.close.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        this.toggleInputs = this.querySelectorAll('[data-input-modal-toggle]');
        this.overlay = this.querySelector('[data-input-modal-overlay]');
        this.closeBtn = this.querySelector('[data-input-modal-close]');
        this.hideYesNo = this.dataset.hideYesNo === 'true';

        this.boundCaptureSubmit = this.onCaptureSubmit.bind(this);
        document.addEventListener('submit', this.boundCaptureSubmit, true);

        if (this.hideYesNo) {
          const openBtn = this.querySelector('[data-input-modal-open]');
          if (openBtn) openBtn.addEventListener('click', () => this.open());
        } else {
          this.toggleInputs.forEach((input) => {
            input.addEventListener('change', () => this.handleToggleChange(input));
          });
        }

        if (this.closeBtn) this.closeBtn.addEventListener('click', this.boundClose);
        if (this.overlay) {
          this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.boundClose();
          });
        }

        if (this.overlay) {
          document.body.appendChild(this.overlay);
        }

        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
          if (evt.source === 'product-form') this.resetAll();
        });

        const doneBtn = this.overlay?.querySelector('[data-input-modal-done]');
        if (doneBtn) {
          doneBtn.addEventListener('click', () => this.close());
        }

        this.overlay?.addEventListener('input', (e) => {
          if (e.target.matches?.('input[data-input-modal-input], select[data-input-modal-input]')) {
            const field = e.target.closest('[data-input-modal-field]');
            if (field) this.clearFieldError(field);
            if (field) this.updateCharCount(field);
            if (e.target.matches?.('input[type="date"][data-input-modal-date-source]')) {
              this.syncDatePropertyFields();
            }
          }
        });
        this.overlay?.addEventListener('change', (e) => {
          if (e.target.matches?.('input[data-input-modal-input], select[data-input-modal-input]')) {
            const field = e.target.closest('[data-input-modal-field]');
            if (field) this.clearFieldError(field);
            if (e.target.matches?.('input[type="date"][data-input-modal-date-source]')) {
              this.syncDatePropertyFields();
            }
          }
        });

        this.syncCharmActive();
        this.overlay?.querySelectorAll('[data-input-modal-field]').forEach((field) => this.updateCharCount(field));
      }

      /** Updates the "0/14" counter of a text field that has a maxlength. */
      updateCharCount(field) {
        const counter = field.querySelector('[data-input-modal-char-count]');
        const input = field.querySelector('input[maxlength][data-input-modal-input]');
        if (!counter || !input) return;
        counter.textContent = `${input.value.length}/${input.maxLength}`;
      }

      /* ========== Charm picker integration ========== */

      charmPickers() {
        return this.overlay ? this.overlay.querySelectorAll('charm-picker') : [];
      }

      /** Charm pickers only contribute items/properties while the toggle is "Yes". */
      syncCharmActive() {
        const active = this.isYesSelected();
        this.charmPickers().forEach((picker) => {
          if (typeof picker.setActive === 'function') picker.setActive(active);
        });
      }

      /** [{ id, quantity }] for selected charms — separate cart line items. */
      getCharmItems() {
        if (!this.isYesSelected()) return [];
        const items = [];
        this.charmPickers().forEach((picker) => {
          if (typeof picker.getItems === 'function') {
            picker.getItems().forEach((item) => items.push(item));
          }
        });
        return items;
      }

      /** True if modal is in "yes" state (details required) */
      isYesSelected() {
        if (this.dataset.hideYesNo === 'true') return true;
        return this.querySelector('[data-input-modal-toggle][value="yes"]')?.checked === true;
      }

      /** Clear error state from a single field wrapper */
      clearFieldError(fieldEl) {
        if (!fieldEl) return;
        fieldEl.classList.remove('input-modal__field--error');
        const err = fieldEl.querySelector('[data-input-modal-field-error-text]');
        if (err) {
          err.textContent = '';
          err.hidden = true;
        }
      }

      /** Clear all field errors in the overlay */
      clearFieldErrors() {
        this.overlay?.querySelectorAll('[data-input-modal-field].input-modal__field--error').forEach((field) => {
          this.clearFieldError(field);
        });
      }

      /**
       * Validate required fields. Call when form is about to submit.
       * @returns {{ valid: boolean, invalidFields: Array<{ fieldEl: HTMLElement, message: string }> }}
       */
      validateRequiredFields() {
        const invalidFields = [];
        const requiredFields = this.overlay?.querySelectorAll('[data-input-modal-field][data-input-modal-field-required="true"]') || [];
        requiredFields.forEach((fieldEl) => {
          if (fieldEl.querySelector('charm-picker')) return; // charm pickers validate themselves below
          const input = fieldEl.querySelector('input[data-input-modal-input], select[data-input-modal-input]');
          const value = input?.value?.trim() ?? '';
          if (value === '') {
            const message = fieldEl.dataset.inputModalFieldError || 'This field is required.';
            invalidFields.push({ fieldEl, message });
          }
        });

        // image-picker-set components paint their own per-row errors internally.
        let pickerSetsValid = true;
        const pickerSets = this.overlay?.querySelectorAll('image-picker-set') || [];
        pickerSets.forEach((pickerSet) => {
          if (typeof pickerSet.validate === 'function' && !pickerSet.validate()) {
            pickerSetsValid = false;
          }
        });

        let symbolPickersValid = true;
        const symbolPickers = this.overlay?.querySelectorAll('symbol-picker') || [];
        symbolPickers.forEach((picker) => {
          if (typeof picker.validate === 'function' && !picker.validate()) {
            symbolPickersValid = false;
          }
        });

        let charmPickersValid = true;
        this.charmPickers().forEach((picker) => {
          if (typeof picker.validate === 'function' && !picker.validate()) {
            charmPickersValid = false;
          }
        });

        return {
          valid: invalidFields.length === 0 && pickerSetsValid && symbolPickersValid && charmPickersValid,
          invalidFields
        };
      }

      /**
       * Open the modal and show errors on the given fields. Clears other field errors first.
       * @param {Array<{ fieldEl: HTMLElement, message: string }>} invalidFields
       */
      openAndShowErrors(invalidFields) {
        this.clearFieldErrors();
        this.open();
        invalidFields.forEach(({ fieldEl, message }) => {
          if (!fieldEl || !message) return;
          fieldEl.classList.add('input-modal__field--error');
          const err = fieldEl.querySelector('[data-input-modal-field-error-text]');
          if (err) {
            err.textContent = message;
            err.hidden = false;
          }
        });
      }

      /** Format place as "City, Country" from address_components */
      formatCityCountry(place) {
        if (!place || !place.address_components) return '';
        const ac = place.address_components;
        const locality = ac.find((c) => c.types.includes('locality'))?.long_name
          || ac.find((c) => c.types.includes('administrative_area_level_1'))?.long_name
          || '';
        const country = ac.find((c) => c.types.includes('country'))?.long_name || '';
        if (!locality && !country) return place.formatted_address || '';
        return country ? `${locality}, ${country}` : locality;
      }

      initLocationAutocomplete() {
        const apiKey = (this.dataset.googleMapsApiKey || '').trim();
        const locationInputs = this.overlay?.querySelectorAll('input[data-input-type="location"]') || [];
        if (!apiKey || locationInputs.length === 0) return;

        const initInputs = () => {
          locationInputs.forEach((input) => {
            if (input.dataset.inputModalAutocompleteInit) return;
            input.dataset.inputModalAutocompleteInit = '1';
            const lastSelected = { value: '' };

            const autocomplete = new window.google.maps.places.Autocomplete(input, {
              types: ['(cities)'],
              fields: ['address_components', 'formatted_address']
            });

            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              const cityCountry = this.formatCityCountry(place);
              if (cityCountry) {
                input.value = cityCountry;
                lastSelected.value = cityCountry;
              }
            });

            input.addEventListener('blur', () => {
              if (lastSelected.value) {
                if (input.value !== lastSelected.value) input.value = lastSelected.value;
              } else if (input.value.trim() !== '') {
                input.value = '';
              }
            });
          });
        };

        if (window.google && window.google.maps && window.google.maps.places) {
          initInputs();
          return;
        }

        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          const check = setInterval(() => {
            if (window.google && window.google.maps && window.google.maps.places) {
              clearInterval(check);
              initInputs();
            }
          }, 100);
          return;
        }

        const callbackName = 'inputModalGoogleMapsCallback';
        window[callbackName] = () => {
          window[callbackName] = null;
          initInputs();
        };
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      formatDateForLineItem(value) {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const [year, month, day] = value.split('-');
        return `${day}.${month}.${year}`;
      }

      syncDatePropertyFields() {
        const dateInputs = this.overlay?.querySelectorAll('input[type="date"][data-input-modal-date-source]') || [];
        dateInputs.forEach((input) => {
          const target = input.parentElement?.querySelector('input[type="hidden"][data-input-modal-date-target]');
          if (!target) return;
          target.value = this.formatDateForLineItem(input.value);
        });
      }

      clearDatePropertyFields() {
        this.overlay?.querySelectorAll('input[type="hidden"][data-input-modal-date-target]').forEach((input) => {
          input.value = '';
        });
      }

      onCaptureSubmit(e) {
        const form = e.target;
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        if (this.formId && form.id !== this.formId) {
          const linkedToCurrentForm = this.overlay?.querySelector(
            `input[data-input-modal-input][form="${form.id}"], select[data-input-modal-input][form="${form.id}"]`
          );
          if (!linkedToCurrentForm) return;
        }
        if (!this.isYesSelected()) return;
        const result = this.validateRequiredFields();
        if (!result.valid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.openAndShowErrors(result.invalidFields);
          return;
        }

        this.syncDatePropertyFields();
        this.maybeHandleCharmSubmit(e, form);
      }

      /**
       * Fallback add-to-cart takeover for charm selections. The interceptors in
       * product-upsell / optional-file-input / backside-engraving-modal register
       * their capture listeners before this one (script order in main-product),
       * so if this code runs none of them took over — without it, a plain form
       * submit would add only the main product and drop the charm line items.
       */
      maybeHandleCharmSubmit(e, form) {
        if (typeof CartAdd === 'undefined') return;
        const section = form.closest('section');
        if (!section) return;
        const items = CartAdd.getUpsellItems(section);
        if (items.length === 0) return;

        // We submit for the whole section, so every other input-modal must be valid too.
        const modals = Array.from(section.querySelectorAll('input-modal'));
        for (const modal of modals) {
          if (modal === this) continue;
          if (typeof modal.isYesSelected !== 'function' || !modal.isYesSelected()) continue;
          const modalResult = modal.validateRequiredFields();
          if (!modalResult.valid) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            modal.openAndShowErrors(modalResult.invalidFields);
            return;
          }
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const productFormEl = form.closest('product-form');
        const submitButton = productFormEl?.querySelector('[type="submit"]');
        const cart = CartAdd.getCart();

        CartAdd.setButtonLoading(submitButton, productFormEl, true);

        const formData = new FormData(form);
        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);
        const properties = CartAdd.buildMainProperties(formData, {
          section,
          skipEngravingWhenNo: true,
          skipUpsellGroupProps: true,
        });
        const mainItem = { id: parseInt(mainVariantId, 10), quantity };
        if (Object.keys(properties).length > 0) mainItem.properties = properties;

        const { sections, sectionsUrl } = CartAdd.getSectionsConfig(cart);
        if (cart) cart.setActiveElement(document.activeElement);

        CartAdd.postJSON([...items, mainItem], sections, sectionsUrl)
          .then((response) => {
            CartAdd.handleResponse(response, { cart, productFormEl, mainVariantId });
          })
          .catch(console.error)
          .finally(() => CartAdd.setButtonLoading(submitButton, productFormEl, false));
      }

      onKeydown(e) {
        if (e.key === 'Escape' && this.overlay && !this.overlay.hidden) this.close();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.boundKeydown);
        if (this.boundCaptureSubmit) document.removeEventListener('submit', this.boundCaptureSubmit, true);
        if (this.cartUpdateUnsub) this.cartUpdateUnsub();
        if (this.overlay && this.overlay.parentNode === document.body) {
          this.overlay.remove();
        }
      }

      handleToggleChange(input) {
        if (input.value === 'yes') {
          this.open();
        } else {
          this.close();
        }
        this.syncCharmActive();
      }

      resetAll() {
        const defaultYes = this.dataset.defaultYes === 'true';
        this.clearDatePropertyFields();
        this.toggleInputs = this.querySelectorAll('[data-input-modal-toggle]');
        this.toggleInputs?.forEach((input) => {
          if (input.value === 'no') input.checked = !defaultYes;
          else if (input.value === 'yes') input.checked = defaultYes;
        });
        if (this.overlay) {
          this.clearFieldErrors();
          this.overlay.querySelectorAll('input[data-input-modal-input], select[data-input-modal-input]').forEach((el) => {
            el.value = '';
            const placeHolder = el.getAttribute('data-input-modal-location-placeholder');
            if (placeHolder != null) el.placeholder = placeHolder;
            delete el.dataset.inputModalAutocompleteInit;
          });
          this.overlay.querySelectorAll('[data-input-modal-field]').forEach((field) => this.updateCharCount(field));
          this.overlay.querySelectorAll('image-picker-set').forEach((pickerSet) => {
            if (typeof pickerSet.reset === 'function') pickerSet.reset();
          });
          this.overlay.querySelectorAll('symbol-picker').forEach((picker) => {
            if (typeof picker.reset === 'function') picker.reset();
          });
          this.charmPickers().forEach((picker) => {
            if (typeof picker.reset === 'function') picker.reset();
          });
        }
        this.syncCharmActive();
        this.close();
      }

      open() {
        if (!this.overlay) return;
        this.clearFieldErrors();
        this.overlay.hidden = false;
        this.overlay.setAttribute('data-open', 'false');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.boundKeydown);
        this.initLocationAutocomplete();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.overlay.setAttribute('data-open', 'true');
          });
        });
      }

      close() {
        if (!this.overlay) return;
        this.overlay.setAttribute('data-open', 'false');
        document.removeEventListener('keydown', this.boundKeydown);
        setTimeout(() => {
          this.overlay.hidden = true;
          document.body.style.overflow = '';
        }, 350);
      }
    }
  );
}
